import { useState } from "react";
import { useMenuAddons, useMenuItems } from "../../../../hooks/useMenu";
import type { Addon, Item } from "../../../../api/menu";
import { addonSortOrder } from "../../../../lib/lunchAddonOptions";

/** addonId -> { itemId -> количество } — същата форма като в кошницата.
 *  При обикновените добавки (тип "1") количеството се регулира с +/− на реда;
 *  при тип "2"/"3" винаги е 1. */
export type AddonSelection = Record<number, Record<number, number>>;

/**
 * Добавките на продукта в „Промени" (EditModifiersModal): избраното,
 * правилото за задължителните и цената им.
 */
export const useAddonSelection = (item: Item | null) => {
  const allAddons = useMenuAddons();
  const allItems = useMenuItems();
  const [selectedAddonItems, setSelectedAddonItems] = useState<AddonSelection>(
    {},
  );

  const addons: Addon[] = item
    ? allAddons
        .filter((addon) => item.addonIds.includes(addon.id))
        .sort((a, b) => addonSortOrder(a) - addonSortOrder(b))
    : [];

  // Брой РАЗЛИЧНИ избрани асортимента в добавка (количествата не влияят на лимита)
  const selectedCountOf = (addonId: number) =>
    Object.keys(selectedAddonItems[addonId] || {}).length;

  // Опциите на добавката, които реално са в менюто на ресторанта (останалите не се
  // показват, затова не може и да се изискват). Изчерпаните от дневно меню СЕ
  // броят: „избери 2" остава 2, дори да е останала една — тогава продуктът
  // изобщо не се предлага (useHiddenLunchItems / refuseRequiredSoldOut).
  const availableOptionsCountOf = (addon: Addon) =>
    addon.items.filter((option) =>
      allItems.some((menuItem) => menuItem.id === option.itemId),
    ).length;
  // Задължителната изисква maxQty различни опции, но не повече от наличните — иначе
  // „Запази“ остава заключен завинаги, без сервитьорът да вижда защо.
  const requiredCountOf = (addon: Addon) =>
    Math.min(addon.maxQty || 1, availableOptionsCountOf(addon));

  // Задължителна добавка без нито една налична опция не блокира продажбата.
  const allRequiredAddonsFulfilled = addons
    .filter((addon) => addon.type === 2 && availableOptionsCountOf(addon) > 0)
    .every((addon) => selectedCountOf(addon.id) >= requiredCountOf(addon));

  // Тип "1" (обикновена) добавките са с АБСОЛЮТНО количество — не се умножават
  // по бройката на продукта. Тип "2"/"3" са по 1 на продукт (легаси) → × quantity.
  const { absoluteAddonsPrice, perProductAddonsPrice } = Object.entries(
    selectedAddonItems,
  ).reduce(
    (acc, [addonId, quantityByItemId]) => {
      const addon = addons.find((a) => a.id === Number(addonId));
      if (!addon) return acc;
      const groupSum = Object.entries(quantityByItemId).reduce(
        (sum, [itemId, quantity]) => {
          const addonItem = addon.items.find(
            (option) => option.itemId === Number(itemId),
          );
          if (!addonItem) return sum;
          const price = addonItem.addonItemPrice ?? addonItem.price ?? 0;
          return sum + price * quantity;
        },
        0,
      );
      if (addon.type === 1) acc.absoluteAddonsPrice += groupSum;
      else acc.perProductAddonsPrice += groupSum;
      return acc;
    },
    { absoluteAddonsPrice: 0, perProductAddonsPrice: 0 },
  );

  return {
    addons,
    selectedAddonItems,
    setSelectedAddonItems,
    selectedCountOf,
    requiredCountOf,
    allRequiredAddonsFulfilled,
    absoluteAddonsPrice,
    perProductAddonsPrice,
  };
};

export type AddonSelectionState = ReturnType<typeof useAddonSelection>;
